// Configuration store for the Concierge chat iframe.
//
// SECURITY NOTE: anything bundled into this app is public — it ships to the
// browser. Never put a long-lived credential here. The bearer token must come
// from one of:
//   1. a server-side proxy that holds the token and is called via PROXY_URL
//      (preferred — the browser never sees the credential), or
//   2. a short-lived token handed to us by a trusted parent page over
//      postMessage (see ALLOWED_PARENT_ORIGINS).

const env = process.env;

const splitOrigins = (value) =>
  (value || '')
    .split(',')
    .map((origin) => origin.trim())
    .filter(Boolean);

// Parent origins allowed to configure this iframe over postMessage.
// Fails closed: with no allowlist configured, no parent config is accepted.
// A single "*" entry allows any parent origin — safe here because the bundle
// carries no credential (the parent supplies its own token/botId) and the
// widget never posts message content back, so an arbitrary embedder gains
// nothing it did not already bring. `event.source === window.parent` still
// restricts configuration to the actual embedding page.
export const ALLOWED_PARENT_ORIGINS = splitOrigins(
  env.REACT_APP_ALLOWED_PARENT_ORIGINS
);

export const ALLOW_ANY_PARENT_ORIGIN = ALLOWED_PARENT_ORIGINS.includes('*');

export function isParentOriginAllowed(origin) {
  return ALLOW_ANY_PARENT_ORIGIN || ALLOWED_PARENT_ORIGINS.includes(origin);
}

if (ALLOWED_PARENT_ORIGINS.length === 0) {
  console.warn(
    '[concierge] REACT_APP_ALLOWED_PARENT_ORIGINS is not set — parent page ' +
      'configuration via postMessage is disabled.'
  );
} else if (ALLOW_ANY_PARENT_ORIGIN) {
  console.warn(
    '[concierge] REACT_APP_ALLOWED_PARENT_ORIGINS is "*" — any parent origin ' +
      'may configure this iframe. Safe only because no credential is bundled.'
  );
}

export const REQUEST_CONFIG = {
  // Request timeout in milliseconds. RAG answers via Bedrock routinely take
  // longer than a generic API call, so the default is deliberately generous.
  TIMEOUT: Number(env.REACT_APP_TIMEOUT_MS) || 60000,

  RETRY: {
    attempts: Number(env.REACT_APP_RETRY_ATTEMPTS) || 3,
    delay: Number(env.REACT_APP_RETRY_DELAY_MS) || 1000
  }
};

// Maximum number of messages retained in the transcript.
export const MAX_MESSAGES = Number(env.REACT_APP_MAX_MESSAGES) || 100;

// Maximum length of a single outgoing message.
export const MAX_MESSAGE_LENGTH =
  Number(env.REACT_APP_MAX_MESSAGE_LENGTH) || 1000;

const DEFAULT_WELCOME_MESSAGE =
  env.REACT_APP_WELCOME_MESSAGE ||
  'Welcome. I am your AI Concierge Assistant, ready to help you with any ' +
    'questions or tasks related to the event.';

const DEFAULT_HEADER_TITLE =
  env.REACT_APP_HEADER_TITLE || 'AI Concierge Chat';

// Preferred: a proxy we control that injects the credential server-side.
// Falls back to calling the MessageMatrix API directly, which requires a
// token supplied by the parent page and a bot id.
//
// The direct route is the concierge-serverless-service messages endpoint:
//   POST {apiBaseUrl}/concierge/management/bot/{botId}/messages
// apiBaseUrl may be a relative path (e.g. "/api/mm") when an Amplify rewrite
// proxies it same-origin — API Gateway has no CORS on this route.
const DEFAULT_PROXY_URL = env.REACT_APP_PROXY_URL || '';
const DEFAULT_API_BASE_URL = env.REACT_APP_API_BASE_URL || '';
const DEFAULT_BOT_ID = env.REACT_APP_BOT_ID || '';

// Guest identification (optional). The embedding page puts the guest in the
// iframe URL, as Eventogy does:
//   <iframe src="https://…amplifyapp.com?eventid=938&email=guest@example.com">
// and the widget looks the guest up at
//   POST {identifyUrl}/chat/event?eventid={eventId}   body: { email }
// purely to personalise the UI. identifyUrl is normally a same-origin path
// (e.g. "/api/eventogy") rewritten to the provider, whose API only accepts
// calls from allowlisted address ranges.
const DEFAULT_IDENTIFY_URL = env.REACT_APP_IDENTIFY_URL || '';

const readGuestFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      eventId: params.get('eventid') || '',
      email: params.get('email') || ''
    };
  } catch {
    return { eventId: '', email: '' };
  }
};

const URL_GUEST = readGuestFromUrl();

if (!DEFAULT_PROXY_URL && !(DEFAULT_API_BASE_URL && DEFAULT_BOT_ID)) {
  console.warn(
    '[concierge] Neither REACT_APP_PROXY_URL nor REACT_APP_API_BASE_URL + ' +
      'REACT_APP_BOT_ID is set — the chat cannot reach a backend until a ' +
      'parent page supplies the missing configuration.'
  );
}

// --- Store -----------------------------------------------------------------
// Held as an immutable snapshot so React can subscribe via
// useSyncExternalStore and re-render when the parent reconfigures us.

let snapshot = Object.freeze({
  proxyUrl: DEFAULT_PROXY_URL,
  apiBaseUrl: DEFAULT_API_BASE_URL,
  botId: DEFAULT_BOT_ID,
  token: '',
  identifyUrl: DEFAULT_IDENTIFY_URL,
  // Provider API key, only ever supplied at runtime by the parent page —
  // never bundled. Not needed when identifyUrl is a proxy that adds it.
  identifyKey: '',
  eventId: URL_GUEST.eventId,
  email: URL_GUEST.email,
  welcomeMessage: DEFAULT_WELCOME_MESSAGE,
  headerTitle: DEFAULT_HEADER_TITLE
});

const listeners = new Set();

export function getConfig() {
  return snapshot;
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setConfig(patch) {
  const next = Object.freeze({ ...snapshot, ...patch });
  const changed = Object.keys(patch).some((key) => snapshot[key] !== next[key]);
  if (!changed) return;
  snapshot = next;
  listeners.forEach((listener) => listener());
}

// Only https endpoints (or same-origin relative paths, for Amplify rewrites)
// are accepted, so a compromised parent cannot downgrade us to plaintext or
// point us at a non-http scheme.
function isAcceptableEndpoint(value) {
  if (typeof value !== 'string' || !value) return false;
  if (value.startsWith('/')) {
    // Same-origin relative path such as "/api/mm". String prefix checks are
    // not enough: WHATWG URL parsing treats "/\evil.com" and "//evil.com" as
    // protocol-relative, so resolve against our own origin and require the
    // origin to be unchanged.
    try {
      const resolved = new URL(value, window.location.href);
      return resolved.origin === window.location.origin;
    } catch {
      return false;
    }
  }
  try {
    const url = new URL(value);
    if (url.protocol === 'https:') return true;
    // Plain http is tolerated for local development only.
    return (
      url.protocol === 'http:' &&
      (url.hostname === 'localhost' || url.hostname === '127.0.0.1')
    );
  } catch {
    return false;
  }
}

/**
 * Resolve the URL a message should be POSTed to, and how to authenticate.
 *
 * Returns null when not enough configuration is present to reach a backend.
 * `useToken` is true only for the direct API route — the proxy holds the
 * credential itself and must never receive one from the browser.
 */
export function resolveEndpoint(config) {
  // Validate here so a build-time REACT_APP_* default is held to the same bar
  // as a parent-supplied value — an unsafe host never receives a request (or
  // the token). Parent config is already validated on the way in; this is the
  // backstop for the build-time path.
  if (config.proxyUrl) {
    if (!isAcceptableEndpoint(config.proxyUrl)) {
      console.warn('[concierge] Ignoring unsafe proxyUrl endpoint.');
      return null;
    }
    return { url: config.proxyUrl, useToken: false };
  }
  if (config.apiBaseUrl && config.botId) {
    if (!isAcceptableEndpoint(config.apiBaseUrl)) {
      console.warn('[concierge] Ignoring unsafe apiBaseUrl endpoint.');
      return null;
    }
    const base = config.apiBaseUrl.replace(/\/+$/, '');
    return {
      url: `${base}/concierge/management/bot/${encodeURIComponent(config.botId)}/messages`,
      useToken: true
    };
  }
  return null;
}

/**
 * Resolve the guest-lookup request, or null when identification is not
 * configured or there is no guest to identify.
 */
export function resolveIdentifyEndpoint(config) {
  if (!config.identifyUrl || !config.email) return null;
  // The provider's event ids are numeric; anything else is a malformed URL
  // from the embedding page, not something to forward.
  if (!/^\d+$/.test(config.eventId)) return null;
  if (!isAcceptableEndpoint(config.identifyUrl)) {
    console.warn('[concierge] Ignoring unsafe identifyUrl endpoint.');
    return null;
  }
  const base = config.identifyUrl.replace(/\/+$/, '');
  return { url: `${base}/chat/event?eventid=${config.eventId}` };
}

/**
 * Apply configuration supplied by a trusted parent page.
 *
 * The caller is responsible for verifying `event.origin` against
 * ALLOWED_PARENT_ORIGINS before invoking this.
 */
export function updateConfigFromParent(config) {
  if (!config || typeof config !== 'object') return;

  const patch = {};

  // `bearerToken` is accepted as a legacy alias for `token`. An explicit
  // empty string is honoured so a parent can clear the credential (logout);
  // only omit the key entirely to leave the token unchanged.
  if ('token' in config && typeof config.token === 'string') {
    patch.token = config.token;
  } else if (
    'bearerToken' in config &&
    typeof config.bearerToken === 'string'
  ) {
    patch.token = config.bearerToken;
  }

  if (typeof config.botId === 'string' && config.botId) {
    patch.botId = config.botId;
  }

  // Same clearing semantics as `token`.
  if ('identifyKey' in config && typeof config.identifyKey === 'string') {
    patch.identifyKey = config.identifyKey;
  }

  // The iframe URL is the usual source of the guest; these let a parent that
  // cannot rewrite the src supply (or correct) them instead. As with `token`,
  // an explicit empty string clears the guest, so a reused iframe stops
  // greeting the previous one.
  if ('eventId' in config && typeof config.eventId === 'string') {
    patch.eventId = config.eventId;
  }

  if ('email' in config && typeof config.email === 'string') {
    patch.email = config.email;
  }

  if (typeof config.welcomeMessage === 'string' && config.welcomeMessage) {
    patch.welcomeMessage = config.welcomeMessage;
  }

  if (typeof config.headerTitle === 'string' && config.headerTitle) {
    patch.headerTitle = config.headerTitle;
  }

  if (typeof config.proxyUrl === 'string' && config.proxyUrl) {
    if (isAcceptableEndpoint(config.proxyUrl)) {
      patch.proxyUrl = config.proxyUrl;
    } else {
      console.warn('[concierge] Rejected non-https proxyUrl from parent page.');
    }
  }

  if (typeof config.apiBaseUrl === 'string' && config.apiBaseUrl) {
    if (isAcceptableEndpoint(config.apiBaseUrl)) {
      patch.apiBaseUrl = config.apiBaseUrl;
    } else {
      console.warn(
        '[concierge] Rejected non-https apiBaseUrl from parent page.'
      );
    }
  }

  setConfig(patch);
}
